import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { fetchProfileData } from '../redux/accountSlice';
import { SECTION_RULES } from '../utils/cartRules';
import { 
  FREE_DELIVERY_THRESHOLD, 
  DELIVERY_CHARGE, 
  PACKING_CHARGE_STANDARD, 
  PACKING_CHARGE_FREE_DELIVERY, 
  MIN_CART_VALUE 
} from '../utils/cartConfig';

// Import Checkout Subcomponents
import AddressCard from '../components/checkout/AddressCard';
import AddressSelectorModal from '../components/checkout/AddressSelectorModal';
import AddressFormModal from '../components/account/AddressFormModal';
import OrderReviewList from '../components/checkout/OrderReviewList';
import CheckoutFooter from '../components/checkout/CheckoutFooter';

import '../styles/checkout.css';

const Checkout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  // Redux state selectors
  const cartItems = useSelector((state) => state.cart.cartItems);
  const appliedCoupon = useSelector((state) => state.cart.appliedCoupon);
  const { addresses, loading: profileLoading } = useSelector((state) => state.account);

  // Component states
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // 1. Route Protection & Initial Data Load
  useEffect(() => {
    if (cartItems.length === 0) {
      navigate('/cart');
      return;
    }
    // Fetch profile data to load saved addresses
    dispatch(fetchProfileData());
  }, [cartItems.length, navigate, dispatch]);

  // 2. Preselect Default Address
  useEffect(() => {
    if (addresses && addresses.length > 0) {
      // Preselect default address, or fallback to first address
      const alreadySelectedExists = selectedAddress && addresses.find(a => a._id === selectedAddress._id);
      if (!alreadySelectedExists) {
        const defaultAddr = addresses.find(addr => addr.isDefault) || addresses[0];
        setSelectedAddress(defaultAddr);
      }
    } else {
      setSelectedAddress(null);
    }
  }, [addresses, selectedAddress]);

  // 3. Calculations (Identical to Cart Page)
  const subtotal = useMemo(() => {
    return cartItems.reduce((acc, item) => acc + (item.price * item.qty), 0);
  }, [cartItems]);

  const discountAmount = useMemo(() => {
    if (!appliedCoupon) return 0;
    if (appliedCoupon.discountType === 'FixedAmount') {
      return Math.min(subtotal, Number(appliedCoupon.discountValue) || 0);
    }
    return Math.round((subtotal * (Number(appliedCoupon.discountValue) || 0)) / 100);
  }, [subtotal, appliedCoupon]);

  const deliveryCharge = useMemo(() => {
    if (cartItems.length === 0) return 0;
    return subtotal >= FREE_DELIVERY_THRESHOLD ? 0 : DELIVERY_CHARGE;
  }, [cartItems.length, subtotal]);

  const packingCharge = useMemo(() => {
    if (cartItems.length === 0) return 0;
    return subtotal >= FREE_DELIVERY_THRESHOLD ? PACKING_CHARGE_FREE_DELIVERY : PACKING_CHARGE_STANDARD;
  }, [cartItems.length, subtotal]);

  const grandTotal = useMemo(() => {
    return subtotal - discountAmount + deliveryCharge + packingCharge;
  }, [subtotal, discountAmount, deliveryCharge, packingCharge]);

  // 4. Section Eligibility & MOQ rules (reused from Cart.jsx)
  const groupedItems = useMemo(() => {
    const groups = {};
    cartItems.forEach((item) => {
      const section = item.section || "Unknown Section";
      if (!groups[section]) {
        groups[section] = [];
      }
      groups[section].push(item);
    });
    return groups;
  }, [cartItems]);

  const sectionsEligibility = useMemo(() => {
    const list = [];
    const coreSections = [
      "National Articles",
      "Premium & Imported",
      "Accessories",
      "Fragrance & Grooming"
    ];

    coreSections.forEach((sectionName) => {
      const items = groupedItems[sectionName] || [];
      const rule = SECTION_RULES[sectionName];

      if (items.length === 0) {
        list.push({
          name: sectionName,
          eligible: true,
          message: "Ready (No items)"
        });
        return;
      }

      if (rule.type === 'none') {
        list.push({
          name: sectionName,
          eligible: true,
          message: "Ready"
        });
      } else if (rule.type === 'moq') {
        const totalQty = items.reduce((acc, item) => acc + item.qty, 0);
        const limit = rule.value;
        const eligible = totalQty >= limit;
        list.push({
          name: sectionName,
          eligible,
          message: eligible ? "MOQ Met" : `Needs ${limit - totalQty} More Pcs`
        });
      } else if (rule.type === 'value') {
        const totalValue = items.reduce((acc, item) => acc + (item.price * item.qty), 0);
        const limit = rule.value;
        const eligible = totalValue >= limit;
        list.push({
          name: sectionName,
          eligible,
          message: eligible ? "Eligible" : `Needs ₹${limit - totalValue} More`
        });
      }
    });

    if (groupedItems["Unknown Section"]) {
      list.push({
        name: "Unknown Section",
        eligible: false,
        message: "Legacy items must be removed"
      });
    }

    return list;
  }, [groupedItems]);

  const isOverallEligible = useMemo(() => {
    if (cartItems.length === 0) return false;
    return sectionsEligibility.every((sec) => sec.eligible) && subtotal >= MIN_CART_VALUE;
  }, [cartItems, sectionsEligibility, subtotal]);

  // 5. Address submission success handler
  const handleAddAddressSuccess = (updatedAddresses) => {
    if (updatedAddresses && updatedAddresses.length > 0) {
      // Select the newly added address (which is set as default because forceDefault is true)
      const newAddr = updatedAddresses.find(addr => addr.isDefault) || updatedAddresses[updatedAddresses.length - 1];
      setSelectedAddress(newAddr);
    }
    setIsAddModalOpen(false);
  };

  // 6. Navigation to Step 2 (Payment Page)
  const handleContinueToPayment = () => {
    if (!selectedAddress) {
      alert("Please select a delivery address.");
      return;
    }
    if (!isOverallEligible) {
      alert("Cart eligibility criteria not met.");
      return;
    }
    // Save selected address to sessionStorage so it can be loaded in the next step
    sessionStorage.setItem('selectedAddress', JSON.stringify(selectedAddress));
    // Navigate to next phase (no order created yet)
    navigate('/checkout/payment', { state: { selectedAddress } });
  };

  // Skip rendering if not authenticated or cart is empty
  if (!user || cartItems.length === 0) return null;

  return (
    <div className="checkout-page-wrapper">
      <div className="checkout-header">
        <h2>Checkout</h2>
        <p>Step 1 of 2: Address & Order Review</p>
      </div>

      <div className="checkout-layout">
        {/* Left Column: Delivery Address */}
        <div className="checkout-left-column">
          <AddressCard
            selectedAddress={selectedAddress}
            onOpenSelector={() => setIsSelectorOpen(true)}
            onOpenAddModal={() => setIsAddModalOpen(true)}
            loading={profileLoading}
          />
        </div>

        {/* Right Column: Order Review List & Totals */}
        <div className="checkout-right-column">
          <OrderReviewList
            items={cartItems}
          />

          <CheckoutFooter
            subtotal={subtotal}
            deliveryCharge={deliveryCharge}
            packingCharge={packingCharge}
            discountAmount={discountAmount}
            appliedCoupon={appliedCoupon}
            grandTotal={grandTotal}
            isOverallEligible={isOverallEligible}
            sectionsEligibility={sectionsEligibility}
            selectedAddress={selectedAddress}
            onContinue={handleContinueToPayment}
          />
        </div>
      </div>

      {/* Address Selector Modal */}
      <AddressSelectorModal
        isOpen={isSelectorOpen}
        onClose={() => setIsSelectorOpen(false)}
        addresses={addresses}
        selectedAddressId={selectedAddress ? selectedAddress._id : null}
        onSelect={(addr) => setSelectedAddress(addr)}
      />

      {/* Reusable Address Creation Modal */}
      <AddressFormModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        token={user.token}
        addressesLength={addresses.length}
        forceDefault={true}
        onSuccess={handleAddAddressSuccess}
      />
    </div>
  );
};

export default Checkout;
